import musicUrl from "../assets/nexus-midgard-ambient.mp3?url";

export const OBSERVATORY_AUDIO_LEVELS = {
  music: 0.3,
  focus: 0.09,
  orbitalFocus: 0.095,
  cosmosFocus: 0.082,
  hover: 0.04,
} as const;

const MUSIC_FADE_IN_SECONDS = 1.8;
const MUSIC_FADE_OUT_SECONDS = 0.42;
const MIN_GAIN = 0.0001;

type WebkitWindow = typeof window & { webkitAudioContext?: typeof AudioContext };

type ObservatoryAudioRuntime = {
  context: AudioContext;
  sfxBus: GainNode;
  musicBus: GainNode;
  limiter: DynamicsCompressorNode;
  music: HTMLAudioElement | null;
  musicSource: MediaElementAudioSourceNode | null;
  pauseTimer: number | null;
  enabled: boolean;
};

let runtime: ObservatoryAudioRuntime | null = null;

function createRuntime() {
  const AudioContextCtor = window.AudioContext ?? (window as WebkitWindow).webkitAudioContext;
  if (!AudioContextCtor) return null;
  const context = new AudioContextCtor();
  const sfxBus = context.createGain();
  const musicBus = context.createGain();
  const limiter = context.createDynamicsCompressor();
  sfxBus.gain.value = 1;
  musicBus.gain.value = MIN_GAIN;
  limiter.threshold.value = -8;
  limiter.knee.value = 8;
  limiter.ratio.value = 4;
  limiter.attack.value = 0.003;
  limiter.release.value = 0.22;
  sfxBus.connect(limiter);
  musicBus.connect(limiter);
  limiter.connect(context.destination);
  return {
    context,
    sfxBus,
    musicBus,
    limiter,
    music: null,
    musicSource: null,
    pauseTimer: null,
    enabled: false,
  } satisfies ObservatoryAudioRuntime;
}

function ensureRuntime() {
  runtime ??= createRuntime();
  return runtime;
}

function ensureMusic(current: ObservatoryAudioRuntime) {
  if (current.music) return current.music;
  const music = new Audio();
  music.preload = "none";
  music.loop = true;
  music.src = musicUrl;
  music.volume = 1;
  const source = current.context.createMediaElementSource(music);
  source.connect(current.musicBus);
  current.music = music;
  current.musicSource = source;
  return music;
}

function rampMusic(current: ObservatoryAudioRuntime, target: number, duration: number) {
  const now = current.context.currentTime;
  const gain = current.musicBus.gain;
  gain.cancelScheduledValues(now);
  gain.setValueAtTime(Math.max(gain.value, MIN_GAIN), now);
  gain.exponentialRampToValueAtTime(Math.max(target, MIN_GAIN), now + duration);
}

function ping(current: ObservatoryAudioRuntime, frequency: number, gainValue: number, duration: number) {
  if (!current.enabled) return;
  const oscillator = current.context.createOscillator();
  const gain = current.context.createGain();
  oscillator.type = "sine";
  oscillator.frequency.value = frequency;
  gain.gain.setValueAtTime(MIN_GAIN, current.context.currentTime);
  gain.gain.exponentialRampToValueAtTime(gainValue, current.context.currentTime + 0.018);
  gain.gain.exponentialRampToValueAtTime(MIN_GAIN, current.context.currentTime + duration);
  oscillator.connect(gain).connect(current.sfxBus);
  oscillator.start();
  oscillator.stop(current.context.currentTime + duration + 0.025);
}

export function primeObservatoryAudio() {
  const current = ensureRuntime();
  if (current) void current.context.resume();
}

export function setObservatorySoundEnabled(enabled: boolean) {
  const current = enabled ? ensureRuntime() : runtime;
  if (!current) return;
  current.enabled = enabled;
  if (current.pauseTimer !== null) {
    window.clearTimeout(current.pauseTimer);
    current.pauseTimer = null;
  }

  if (!enabled) {
    rampMusic(current, MIN_GAIN, MUSIC_FADE_OUT_SECONDS);
    current.pauseTimer = window.setTimeout(() => {
      if (!current.enabled) current.music?.pause();
      current.pauseTimer = null;
    }, Math.ceil(MUSIC_FADE_OUT_SECONDS * 1000) + 40);
    return;
  }

  void current.context.resume();
  const music = ensureMusic(current);
  const playback = music.play();
  void playback
    .then(() => {
      if (current.enabled) rampMusic(current, OBSERVATORY_AUDIO_LEVELS.music, MUSIC_FADE_IN_SECONDS);
    })
    .catch(() => {
      // The explicit SOUND control primes the AudioContext in the originating user gesture.
      // If a browser still rejects playback, keep the SFX path functional without surfacing a runtime error.
    });
}

export function playObservatoryFocusTone(kind: "cosmos" | "node" | "orbital") {
  const current = runtime;
  if (!current?.enabled) return;
  if (kind === "orbital") {
    ping(current, 440, OBSERVATORY_AUDIO_LEVELS.orbitalFocus, 0.42);
    return;
  }
  if (kind === "node") {
    ping(current, 196, OBSERVATORY_AUDIO_LEVELS.focus, 0.42);
    return;
  }
  ping(current, 132, OBSERVATORY_AUDIO_LEVELS.cosmosFocus, 0.42);
}

export function playObservatoryHoverTone() {
  const current = runtime;
  if (current?.enabled) ping(current, 620, OBSERVATORY_AUDIO_LEVELS.hover, 0.14);
}

export function disposeObservatoryAudio() {
  const current = runtime;
  runtime = null;
  if (!current) return;
  current.enabled = false;
  if (current.pauseTimer !== null) window.clearTimeout(current.pauseTimer);
  current.music?.pause();
  current.musicSource?.disconnect();
  current.sfxBus.disconnect();
  current.musicBus.disconnect();
  current.limiter.disconnect();
  void current.context.close();
}
